import { Controller } from "@hotwired/stimulus"

// Pending GRDU requests queue on the Users panel.
// Fetches/updates via existing GrduRequestsController JSON endpoints.
export default class extends Controller {
  static targets = [
    "autoAdd",
    "promoteSelected",
    "banners",
    "error",
    "capacityNote",
    "autoNote",
    "loading",
    "tablePanel",
    "tbody",
    "selectAll",
    "selectAllHeader",
    "headerRow",
  ]

  static values = {
    indexUrl: String,
    updateSettingsUrl: String,
    promoteSelectedUrl: String,
  }

  connect() {
    this.selectedIds = new Set()
    this.busy = false
    this.queue = {
      grdu_requests: [],
      capacity: { at_capacity: false, remaining_slots: null },
      auto_add_grdu_requests: false,
    }
    this.loadQueue()
  }

  async loadQueue() {
    this.setLoading(true)
    this.clearError()
    try {
      const data = await this.request(this.indexUrlValue, { method: "GET" })
      this.applyPayload(data)
    } catch (err) {
      this.showError(err.message || "Unable to load pending enrollment requests.")
    } finally {
      this.setLoading(false)
    }
  }

  async toggleAutoAdd(event) {
    const enabled = event.target.checked
    this.queue.auto_add_grdu_requests = enabled
    if (enabled) this.selectedIds.clear()
    await this.runAction(() =>
      this.request(this.updateSettingsUrlValue, {
        method: "PATCH",
        body: JSON.stringify({ auto_add_grdu_requests: enabled }),
      })
    )
  }

  toggleSelectAll() {
    const limit = this.selectionLimit()
    const ids = this.queue.grdu_requests.map((row) => String(row.id))
    if (this.selectedIds.size >= Math.min(limit, ids.length)) {
      this.selectedIds.clear()
    } else {
      this.selectedIds = new Set(ids.slice(0, limit))
    }
    this.renderRows()
    this.syncControls()
  }

  toggleRow(event) {
    const checkbox = event.target
    if (!checkbox.matches?.('input[type="checkbox"][data-testid^="select-grdu-request-"]')) return

    const id = String(checkbox.value)
    if (checkbox.checked) {
      if (this.selectedIds.size >= this.selectionLimit()) {
        checkbox.checked = false
        return
      }
      this.selectedIds.add(id)
    } else {
      this.selectedIds.delete(id)
    }
    this.renderRows()
    this.syncControls()
  }

  async promoteSelected() {
    if (this.selectedIds.size === 0) return
    await this.runAction(() =>
      this.request(this.promoteSelectedUrlValue, {
        method: "POST",
        body: JSON.stringify({ ids: Array.from(this.selectedIds).map((id) => Number(id)) }),
      })
    )
  }

  dismissBanner(event) {
    const banner = event.target.closest("[data-banner-id]")
    if (banner) banner.remove()
  }

  // --- internals ---

  async runAction(action) {
    this.busy = true
    this.clearError()
    this.syncControls()
    try {
      const data = await action()
      this.applyPayload(data)
    } catch (err) {
      this.showError(err.message || "Unable to update pending enrollment requests. Please try again.")
    } finally {
      this.busy = false
      this.syncControls()
    }
  }

  applyPayload(data) {
    this.queue = {
      grdu_requests: data.grdu_requests || [],
      capacity: data.capacity || { at_capacity: false, remaining_slots: null },
      auto_add_grdu_requests: !!data.auto_add_grdu_requests,
    }

    const validIds = new Set(this.queue.grdu_requests.map((row) => String(row.id)))
    this.selectedIds = new Set([...this.selectedIds].filter((id) => validIds.has(String(id))))

    this.renderBanners(data.alerts || [], data.notices || [])
    this.renderRows()
    this.syncControls()
    this.clearError()
  }

  renderRows() {
    const showSelection = this.showSelection()
    const requests = this.queue.grdu_requests
    const limit = this.selectionLimit()

    if (this.hasSelectAllHeaderTarget) {
      this.selectAllHeaderTarget.hidden = !showSelection
    }

    if (!requests.length) {
      const cols = showSelection ? 4 : 3
      this.tbodyTarget.innerHTML = `<tr data-testid="grdu-request-queue-empty"><td colspan="${cols}">No pending enrollment requests.</td></tr>`
      return
    }

    this.tbodyTarget.innerHTML = requests
      .map((request) => {
        const selected = this.selectedIds.has(String(request.id))
        const disabled = this.busy || (!selected && this.selectedIds.size >= limit)
        const checkboxCell = showSelection
          ? `<td class="checkbox">
              <input
                type="checkbox"
                value="${request.id}"
                ${selected ? "checked" : ""}
                ${disabled ? "disabled" : ""}
                aria-label="Select ${this.escapeAttr(request.email)}"
                data-testid="select-grdu-request-${request.id}"
              >
            </td>`
          : ""

        return `<tr data-testid="grdu-request-row-${request.id}">
          ${checkboxCell}
          <td class="email">${this.escapeHtml(request.email)}</td>
          <td class="role">${this.escapeHtml(request.role)}</td>
          <td class="date-added">${this.formatDate(request.created_at)}</td>
        </tr>`
      })
      .join("")
  }

  renderBanners(alerts, notices) {
    if (!this.hasBannersTarget) return
    const parts = []
    alerts.forEach((message, index) => {
      if (message) parts.push(this.bannerHtml("error", `alert-${index}`, message))
    })
    notices.forEach((message, index) => {
      if (message) parts.push(this.bannerHtml("notice", `notice-${index}`, message))
    })
    this.bannersTarget.innerHTML = parts.join("")
  }

  bannerHtml(type, id, message) {
    return `<div class="alert alert-${type}" data-banner-id="${id}">
      ${this.escapeHtml(message)}
      <span class="close" data-action="click->grdu-request-queue#dismissBanner">x</span>
    </div>`
  }

  syncControls() {
    const autoAdd = !!this.queue.auto_add_grdu_requests
    const seats = this.remainingSeats()
    const hasRequests = this.queue.grdu_requests.length > 0
    const showSelection = this.showSelection()

    if (this.hasAutoAddTarget) {
      this.autoAddTarget.checked = autoAdd
      this.autoAddTarget.disabled = this.busy
    }

    if (this.hasPromoteSelectedTarget) {
      this.promoteSelectedTarget.hidden = !showSelection
      this.promoteSelectedTarget.disabled = this.busy || this.selectedIds.size === 0
    }

    if (this.hasAutoNoteTarget) {
      this.autoNoteTarget.hidden = !autoAdd
    }

    if (this.hasCapacityNoteTarget) {
      this.capacityNoteTarget.hidden = !( !autoAdd && seats === 0 && hasRequests )
    }

    if (this.hasSelectAllTarget) {
      const limit = this.selectionLimit()
      const expected = Math.min(limit, this.queue.grdu_requests.length)
      this.selectAllTarget.checked = showSelection && hasRequests && this.selectedIds.size > 0 && this.selectedIds.size === expected
      this.selectAllTarget.disabled = this.busy || !hasRequests
    }
  }

  showSelection() {
    return !this.queue.auto_add_grdu_requests && this.remainingSeats() > 0 && this.queue.grdu_requests.length > 0
  }

  remainingSeats() {
    const capacity = this.queue.capacity
    if (!capacity) return 0
    if (capacity.remaining_slots === null || capacity.remaining_slots === undefined) {
      return Number.POSITIVE_INFINITY
    }
    return capacity.remaining_slots
  }

  selectionLimit() {
    const seats = this.remainingSeats()
    return Number.isFinite(seats) ? seats : this.queue.grdu_requests.length
  }

  setLoading(loading) {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = !loading
    if (this.hasTablePanelTarget) this.tablePanelTarget.hidden = loading
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.hidden = false
    this.errorTarget.textContent = message
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ""
  }

  async request(url, options = {}) {
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      ...options,
    })

    let data = {}
    const text = await response.text()
    if (text.trim()) {
      try {
        data = JSON.parse(text)
      } catch (_e) {
        data = {}
      }
    }

    if (!response.ok) {
      throw new Error(data.error || "Request failed")
    }
    return data
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  formatDate(iso) {
    return iso ? String(iso).split("T")[0] : ""
  }

  escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  escapeAttr(value) {
    return this.escapeHtml(value).replace(/'/g, "&#39;")
  }
}
